import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import { useAuth } from "react-oidc-context";

import { type PartitionDownload } from "./externalRegisterImportCsv";
import { useExternalRegisterImportController, type ExternalRegisterImportController } from "./externalRegisterImportController";
import { sessionDraftAdapter } from "./externalRegisterImportDraft";
import { ExternalRegisterImportEffects } from "./externalRegisterImportEffects";
import {
  importHistoryAdapter,
  type ExternalRegisterImportHistorySummary,
} from "../history/externalRegisterImportHistory";
import {
  filterPreviewRows,
  getMappingErrors,
  getStageStatus,
  getSummaryCounts,
  getTotalPages,
  getVisiblePageRows,
  hasActiveDraft,
  IMPORT_STAGES,
  type ImportStage,
  type ImportStageStatus,
  type SummaryCounts,
} from "./externalRegisterImportModel";
import {
  createInitialImportState,
  externalRegisterImportReducer,
  type ExternalRegisterImportState,
} from "./externalRegisterImportReducer";
import type {
  ExternalRegisterImportAdapter,
  ImportCommitRequest,
  ImportCommitResponse,
  ImportMapping,
  ImportParseResponse,
  ImportPreviewRow,
  ImportReviewFilter,
  ImportValidateResponse,
} from "./externalRegisterImportTypes";

export type ExternalRegisterImportContextValue = ExternalRegisterImportState & ExternalRegisterImportController & {
  latestCommitResponse: ImportCommitResponse | null;
  mappingErrors: string[];
  summaryCounts: SummaryCounts;
  filteredRows: ImportPreviewRow[];
  visibleRows: ImportPreviewRow[];
  totalPages: number;
  draftIsActive: boolean;
  reviewReadonly: boolean;
  stageStatuses: Record<ImportStage, ImportStageStatus>;
  clientImportId: string;
  csvText: string;
  sourceFileName: string | null;
  sourceFileSize: number | null;
  hasHeader: boolean | null;
  mapping: ImportMapping;
  parseResponse: ImportParseResponse | null;
  validateResponse: ImportValidateResponse | null;
  previewRows: ImportPreviewRow[];
  approvedRowIds: Set<string>;
  excludedRowIds: Set<string>;
  selectedPatientIds: Record<string, string>;
  activeFilter: ImportReviewFilter;
  page: number;
  isReadingCsvFile: boolean;
  isParsing: boolean;
  isValidating: boolean;
  commitResponses: ImportCommitResponse[];
  isCommitting: boolean;
  partitionDownloads: PartitionDownload[];
  errorMessage: string | null;
  activeStage: ImportStage;
  historySummaries: ExternalRegisterImportHistorySummary[];
};

type ExternalRegisterImportProviderProps = {
  adapter: ExternalRegisterImportAdapter;
  enableNavigationBlocker?: boolean;
  commitProgressDelayMs?: number | null;
  onCommitRequest?: (request: ImportCommitRequest) => void;
  children: ReactNode;
};

const ExternalRegisterImportContext = createContext<ExternalRegisterImportContextValue | null>(null);

export const ExternalRegisterImportProvider = ({
  adapter,
  enableNavigationBlocker = true,
  commitProgressDelayMs = 0,
  onCommitRequest,
  children,
}: ExternalRegisterImportProviderProps) => {
  const auth = useAuth();
  const [state, dispatch] = useReducer(
    externalRegisterImportReducer,
    null,
    () => createInitialImportState({
      restoredDraft: sessionDraftAdapter.load(),
      historySummaries: importHistoryAdapter.load(),
    }),
  );

  const latestCommitResponse = state.commitResponses[state.commitResponses.length - 1] ?? null;
  const mappingErrors = useMemo(() => getMappingErrors(state.mapping), [state.mapping]);
  const summaryCounts = useMemo(() => getSummaryCounts(state.previewRows, state.excludedRowIds), [state.previewRows, state.excludedRowIds]);
  const filteredRows = useMemo(() => filterPreviewRows(state.previewRows, state.activeFilter), [state.previewRows, state.activeFilter]);
  const visibleRows = useMemo(() => getVisiblePageRows(filteredRows, state.page), [filteredRows, state.page]);
  const totalPages = getTotalPages(filteredRows.length);
  const reviewReadonly = latestCommitResponse?.status === "completed" && !latestCommitResponse.simulated;
  const draftIsActive = !reviewReadonly && hasActiveDraft(state.csvText, state.parseResponse, state.validateResponse);
  const stageStatuses = useMemo(() => {
    return IMPORT_STAGES.reduce((statuses, stage) => ({
      ...statuses,
      [stage.value]: getStageStatus({
        stage: stage.value,
        csvText: state.csvText,
        parseResponse: state.parseResponse,
        mappingErrors,
        validateResponse: state.validateResponse,
        latestCommitResponse,
      }),
    }), {} as Record<ImportStage, ImportStageStatus>);
  }, [latestCommitResponse, mappingErrors, state.csvText, state.parseResponse, state.validateResponse]);

  const controller = useExternalRegisterImportController({
    adapter,
    commitProgressDelayMs,
    dispatch,
    mappingErrors,
    onCommitRequest,
    reviewReadonly,
    state,
  });

  const value = useMemo<ExternalRegisterImportContextValue>(() => ({
    ...state,
    ...controller,
    latestCommitResponse,
    mappingErrors,
    summaryCounts,
    filteredRows,
    visibleRows,
    totalPages,
    draftIsActive,
    reviewReadonly,
    stageStatuses,
  }), [
    controller,
    draftIsActive,
    filteredRows,
    latestCommitResponse,
    mappingErrors,
    reviewReadonly,
    stageStatuses,
    state,
    summaryCounts,
    totalPages,
    visibleRows,
  ]);

  return (
    <ExternalRegisterImportContext.Provider value={value}>
      <ExternalRegisterImportEffects
        auth={auth}
        dispatch={dispatch}
        draftIsActive={draftIsActive}
        enableNavigationBlocker={enableNavigationBlocker}
        state={state}
      />
      {children}
    </ExternalRegisterImportContext.Provider>
  );
};

export const useExternalRegisterImport = (): ExternalRegisterImportContextValue => {
  const context = useContext(ExternalRegisterImportContext);
  if (!context) {
    throw new Error("useExternalRegisterImport must be used inside ExternalRegisterImportProvider");
  }
  return context;
};
